/**
 * PRIVACY POLICY PAGE
 * Legal privacy policy for SkillProof Assessment Platform
 * 8ANDH ENGINEERING SERVICES PRIVATE LIMITED (We Remote Devs)
 */

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = "January 17, 2026";

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-8 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card>
          <CardContent className="p-4 sm:p-8 prose prose-sm max-w-none break-words">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-8 h-8 text-purple-600" />
              <h1 className="text-2xl sm:text-3xl font-bold m-0">Privacy Policy</h1>
            </div>

            <p className="text-gray-500 mb-8">Last Updated: {lastUpdated}</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">1. INTRODUCTION AND SCOPE</h2>
              <p>
                8ANDH ENGINEERING SERVICES PRIVATE LIMITED, a company incorporated under the laws of India, 
                operating under the trade name "We Remote Devs" and its assessment platform "SkillProof" 
                (hereinafter referred to as "Company," "we," "us," or "our"), is committed to protecting 
                the privacy and personal data of individuals who access or use our technical assessment 
                platform and related services (collectively, the "Services").
              </p>
              <p>
                This Privacy Policy ("Policy") describes how we collect, use, process, store, disclose, 
                and protect personal information obtained through our platform located at test.weremotedevs.com 
                and any associated applications, websites, or services. By accessing or using our Services, 
                you acknowledge that you have read, understood, and agree to be bound by the terms of this Policy.
              </p>
              <p>
                This Policy is incorporated into and forms an integral part of our Terms and Conditions. 
                Capitalized terms not defined herein shall have the meanings ascribed to them in the Terms and Conditions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">2. DATA CONTROLLER INFORMATION</h2>
              <p>The data controller responsible for your personal data is:</p>
              <div className="bg-gray-100 p-4 rounded-lg my-4">
                <p className="font-semibold mb-1">8ANDH ENGINEERING SERVICES PRIVATE LIMITED</p>
                <p className="mb-1">Q5H4, Bhartiya City, Kannuru</p>
                <p className="mb-1">Bangalore, Karnataka 560064, India</p>
                <p>Email: business@weremotedevs.com</p>
              </div>
              <p>
                For any inquiries, concerns, or requests regarding your personal data or this Policy, 
                please contact us at the email address provided above.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">3. CATEGORIES OF PERSONAL DATA COLLECTED</h2>
              <p>We collect and process the following categories of personal data:</p>
              
              <h3 className="text-lg font-medium mt-4 mb-2">3.1 Information Provided Directly</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Full legal name as provided by you or your prospective employer</li>
                <li>Email address for authentication and communication purposes</li>
                <li>Professional information including current role, experience level, and skills</li>
                <li>Assessment responses, code submissions, and written answers</li>
                <li>Any additional information voluntarily provided during the assessment process</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">3.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Device information including browser type, operating system, and screen resolution</li>
                <li>IP address and approximate geographic location derived therefrom</li>
                <li>Browser fingerprint data for fraud prevention and session management</li>
                <li>Timestamps of access, session duration, and interaction patterns</li>
                <li>Technical logs and diagnostic information</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">3.3 Proctoring and Monitoring Data</h3>
              <p className="font-medium text-red-700">
                BY PROCEEDING WITH THE ASSESSMENT, YOU EXPRESSLY CONSENT TO THE COLLECTION OF THE FOLLOWING:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Live webcam video feed and periodic photographic captures during the assessment</li>
                <li>Audio recordings from your device microphone for voice activity detection</li>
                <li>Screen activity monitoring including tab switches, window focus changes, and copy-paste attempts</li>
                <li>Facial recognition data for identity verification and attention monitoring</li>
                <li>Object detection data including detection of mobile phones, additional persons, or prohibited materials</li>
                <li>Behavioral biometrics including typing patterns and mouse movements</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">4. PURPOSE AND LEGAL BASIS FOR PROCESSING</h2>
              <p>We process your personal data for the following purposes:</p>
              
              <h3 className="text-lg font-medium mt-4 mb-2">4.1 Performance of Contract</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>To provide and administer the technical assessment services</li>
                <li>To authenticate your identity and authorize access to the platform</li>
                <li>To evaluate your technical competencies and generate assessment reports</li>
                <li>To communicate assessment results to authorized parties</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">4.2 Legitimate Interests</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>To maintain the integrity and security of the assessment process</li>
                <li>To detect, prevent, and investigate fraud, cheating, or unauthorized assistance</li>
                <li>To improve our Services, algorithms, and assessment methodologies</li>
                <li>To protect our legal rights and interests</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">4.3 Consent</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Collection and processing of proctoring data (webcam, microphone, screen monitoring)</li>
                <li>Use of artificial intelligence for answer evaluation and report generation</li>
                <li>Sharing of assessment results with prospective employers</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">4.4 Legal Obligations</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Compliance with applicable laws, regulations, and legal processes</li>
                <li>Response to lawful requests from governmental authorities</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">5. ARTIFICIAL INTELLIGENCE AND AUTOMATED PROCESSING</h2>
              <p>
                Our platform utilizes artificial intelligence ("AI") and machine learning technologies 
                for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Automated generation of assessment questions based on job requirements</li>
                <li>Evaluation and scoring of candidate responses including code submissions and written answers</li>
                <li>Generation of detailed assessment reports and recommendations</li>
                <li>Real-time proctoring including face detection, object detection, and anomaly identification</li>
                <li>Voice activity detection and analysis</li>
              </ul>
              <p className="mt-4">
                You acknowledge and consent to decisions being made, in whole or in part, through automated 
                means. While we strive for accuracy, AI-generated evaluations may contain errors or 
                inconsistencies. Final hiring decisions remain the sole responsibility of the prospective 
                employer, and we disclaim any liability for employment outcomes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">6. DATA SHARING AND DISCLOSURE</h2>
              <p>We may share your personal data with the following categories of recipients:</p>
              
              <h3 className="text-lg font-medium mt-4 mb-2">6.1 Prospective Employers</h3>
              <p>
                Assessment results, scores, reports, proctoring summaries, and related information will 
                be shared with the organization that initiated your assessment. This sharing is essential 
                for the performance of the Services.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">6.2 Service Providers</h3>
              <p>We engage third-party service providers who process data on our behalf, including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Cloud infrastructure providers (Supabase, Cloudflare, Amazon Web Services)</li>
                <li>AI and machine learning service providers (OpenAI)</li>
                <li>Email delivery services (Brevo)</li>
                <li>Analytics and monitoring services</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">6.3 Legal Requirements</h3>
              <p>
                We may disclose your information if required by law, court order, or governmental 
                regulation, or if we believe in good faith that such disclosure is necessary to 
                protect our rights, your safety, or the safety of others.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">7. INTERNATIONAL DATA TRANSFERS</h2>
              <p>
                Your personal data may be transferred to and processed in countries outside of India, 
                including the United States and European Union member states, where our service providers 
                maintain facilities. By using our Services, you consent to such transfers. We implement 
                appropriate safeguards including contractual protections to ensure your data receives 
                adequate protection regardless of location.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">8. DATA RETENTION</h2>
              <p>
                We retain your personal data for as long as necessary to fulfill the purposes outlined 
                in this Policy, unless a longer retention period is required or permitted by law. 
                Specifically:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Assessment data and reports: Retained for a minimum of 24 months from the assessment date</li>
                <li>Proctoring recordings and images: Retained for 12 months from the assessment date</li>
                <li>Account and authentication data: Retained for the duration of the business relationship plus 36 months</li>
                <li>Technical logs: Retained for 90 days</li>
              </ul>
              <p className="mt-4">
                Upon expiration of the retention period, data will be securely deleted or anonymized 
                unless retention is required for legal or regulatory compliance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">9. DATA SECURITY</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal 
                data against unauthorized access, alteration, disclosure, or destruction. These measures 
                include:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Encryption of data in transit using TLS 1.3</li>
                <li>Encryption of data at rest using AES-256</li>
                <li>Row-level security policies in our database</li>
                <li>Regular security assessments and vulnerability testing</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Employee training on data protection</li>
              </ul>
              <p className="mt-4">
                Notwithstanding our security measures, no method of transmission over the Internet or 
                electronic storage is completely secure. We cannot guarantee absolute security of your data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">10. YOUR RIGHTS</h2>
              <p>Subject to applicable law, you may have the following rights regarding your personal data:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Right to Access:</strong> Request information about the personal data we hold about you</li>
                <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your personal data, subject to legal retention requirements</li>
                <li><strong>Right to Restrict Processing:</strong> Request limitation of processing in certain circumstances</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time, without affecting the lawfulness of prior processing</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at business@weremotedevs.com. We will 
                respond to your request within 30 days. Note that withdrawal of consent or deletion 
                requests may result in inability to complete or retain assessment records.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">11. COOKIES AND TRACKING TECHNOLOGIES</h2>
              <p>
                Our platform uses cookies and similar technologies for authentication, session management, 
                and security purposes. Essential cookies are required for the platform to function and 
                cannot be disabled. By using our Services, you consent to the use of these technologies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">12. CHILDREN'S PRIVACY</h2>
              <p>
                Our Services are not intended for individuals under the age of 18. We do not knowingly 
                collect personal data from minors. If you believe we have inadvertently collected data 
                from a minor, please contact us immediately for removal.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">13. CHANGES TO THIS POLICY</h2>
              <p>
                We reserve the right to modify this Policy at any time. Changes will be effective upon 
                posting to our platform with an updated "Last Updated" date. Your continued use of the 
                Services following any changes constitutes acceptance of the modified Policy. We encourage 
                you to review this Policy periodically.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">14. GOVERNING LAW AND JURISDICTION</h2>
              <p>
                This Policy shall be governed by and construed in accordance with the laws of India, 
                without regard to its conflict of law provisions. Any disputes arising out of or in 
                connection with this Policy shall be subject to the exclusive jurisdiction of the 
                courts located in Bangalore, Karnataka, India.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">15. CONTACT INFORMATION</h2>
              <p>
                For any questions, concerns, or requests regarding this Privacy Policy or our data 
                practices, please contact:
              </p>
              <div className="bg-gray-100 p-4 rounded-lg my-4">
                <p className="font-semibold mb-1">Data Protection Officer</p>
                <p className="mb-1">8ANDH ENGINEERING SERVICES PRIVATE LIMITED</p>
                <p className="mb-1">Q5H4, Bhartiya City, Kannuru</p>
                <p className="mb-1">Bangalore, Karnataka 560064, India</p>
                <p>Email: business@weremotedevs.com</p>
              </div>
            </section>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-500 text-center">
                © {new Date().getFullYear()} 8ANDH ENGINEERING SERVICES PRIVATE LIMITED. All rights reserved.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
